import './perks-post-info.component.scss';

import { h } from 'preact';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { LegacyAuthModalState } from '../../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../../auth/services/authentication-flow.service';
import {
  selectAccountSettings,
  selectAccountSettingsLoaded,
  selectAccountSettingsLoading,
  selectIsAuthenticated,
} from '../../../../auth/store/auth.selectors';
import { loadAccountSettings } from '../../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../../common/components/spinner/spinner.component';
import { Pageable } from '../../../../common/page/pageable.model';
import { useTranslation } from '../../../../i18n';
import type { Membership, Tier } from '../../../../membership/models/membership.model';
import {
  selectMemberships,
  selectMembershipsLoaded,
  selectMembershipsLoading,
} from '../../../../membership/store/membership.selectors';
import { loadMemberships } from '../../../../membership/store/membership.thunk';
import type { AccountSettingsResponse } from '../../../../models/common.model';
import { selectMusicHasActivated } from '../../../../music/store/music.selectors';
import { NeedAccessButton } from '../../../../need-access/components/need-access-button/need-access-button.component';
import { NeedAccessContentType } from '../../../../need-access/need-access.interface';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import type { Post } from '../../../model/post.interface';
import { PerksHelper } from '../../../perks.helper';
import {
  selectPostById,
  selectPostComments,
  selectPostCommentsLoading,
  selectPostsLoaded,
  selectPostsLoading,
} from '../../../store/perks.selectors';
import { loadPost, loadPostComments, loadPostCounts } from '../../../store/perks.thunk';
import { PerksPopularPosts } from '../../perks-popular-posts/perks-popular-posts.component';
import { PerksPostsSidebar } from '../../perks-posts-sidebar/perks-posts-sidebar.component';
import { requestSubmissionForm } from '../pending-submission';
import { PerksPostComponent } from '../perks-post/perks-post.component';
import {
  clearCommentDeepLink,
  type CommentDeepLink,
  nextDeepLinkStep,
  readCommentDeepLink,
} from '../perks-post-comments/comment-deep-link';
import {
  arriveAtComments,
  consumePendingCommentEntry,
  scrollToComment,
} from '../perks-post-comments/comment-entry';
import { PerksPostComments } from '../perks-post-comments/perks-post-comments.component';
import { MobilePostCarousel } from './mobile-post-carousel.component';

export interface PerksPostInfoProps {
  id: string;
}

export const PerksPostInfoComponent = ({ id }: PerksPostInfoProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const post: Post = useAppSelector(selectPostById(id))!;
  const loading: boolean = useAppSelector(selectPostsLoading);
  const loaded: boolean = useAppSelector(selectPostsLoaded);
  const memberships: Membership[] = useAppSelector(selectMemberships)!;
  const membershipsLoaded: boolean = useAppSelector(selectMembershipsLoaded);
  const membershipsLoading: boolean = useAppSelector(selectMembershipsLoading);
  const musicHasActivated = useAppSelector(selectMusicHasActivated);
  const accountSettingsLoaded = useAppSelector(selectAccountSettingsLoaded);
  const accountSettingsLoading = useAppSelector(selectAccountSettingsLoading);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};
  const [, setLocation] = useHashLocation();
  const commentsRef = useRef<HTMLDivElement>(null);
  const wasLoadingRef = useRef(false);
  const comments = useAppSelector(selectPostComments(id));
  const commentsLoading = useAppSelector(selectPostCommentsLoading(id));
  // A notification email's "View Comment" link, consumed once the comments are in.
  const deepLinkRef = useRef<CommentDeepLink | null>(readCommentDeepLink(window.location.search));
  const deepLinkPagesRef = useRef(0);
  const countsLoading = post?.counts?.loading ?? true;
  const showLoading = loading || countsLoading;

  useEffect(() => {
    document.body.setAttribute('data-post-detail', '');
    return () => {
      document.body.removeAttribute('data-post-detail');
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    // Arriving from the feed the post is already in the store, so the first
    // paint is real content — but the loadPost below swaps it for a spinner
    // and back, which unmounts anything scrolled to or focused meanwhile. Act
    // once that round trip finishes and the layout is final.
    if (showLoading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;

    const entry = consumePendingCommentEntry();
    if ((entry.scroll || entry.focus) && commentsRef.current) {
      arriveAtComments(commentsRef.current, entry);
    }
  }, [showLoading]);

  useEffect(() => {
    dispatch(loadPost(id));
  }, [dispatch, id]);

  useEffect(() => {
    // Bring the deep-linked comment into view: it may sit past the first page of comments
    // or of its parent's replies, so fetch pages until it renders, then scroll and flash it.
    // Falls back to the top of the comments when it cannot be found (deleted, or too deep).
    const link = deepLinkRef.current;
    if (!link || showLoading || commentsLoading || !comments) return;
    const step = nextDeepLinkStep(comments, link, deepLinkPagesRef.current);
    if (step.kind === 'load-page') {
      deepLinkPagesRef.current += 1;
      dispatch(
        loadPostComments({
          postId: id,
          parentCommentId: step.parentCommentId,
          commentDepth: PerksHelper.COMMENTS_DEFAULT_DEPTH,
          childPageSize: PerksHelper.COMMENTS_CHILD_DEFAULT_PAGE_SIZE,
          pageable: Pageable.of(
            step.page,
            step.parentCommentId
              ? PerksHelper.COMMENTS_CHILD_DEFAULT_PAGE_SIZE
              : PerksHelper.COMMENTS_DEFAULT_PAGE_SIZE,
            PerksHelper.COMMENTS_DEFAULT_SORT
          ),
        })
      );
      return;
    }
    deepLinkRef.current = null;
    clearCommentDeepLink();
    if (step.kind === 'found') {
      scrollToComment(link.commentId, { highlight: true });
    } else if (commentsRef.current) {
      arriveAtComments(commentsRef.current, { scroll: true, focus: false });
    }
  }, [comments, commentsLoading, showLoading, dispatch, id]);

  useEffect(() => {
    // The join card needs memberships and account settings; fetch each once.
    // These stay off the loadPost effect: the video player re-requests account
    // settings on every mount (loaded flips false while pending), and reloading
    // the post in response swapped the page to its spinner and back forever.
    if (!membershipsLoaded && !membershipsLoading) {
      dispatch(loadMemberships());
    }
    if (!accountSettingsLoaded && !accountSettingsLoading) {
      dispatch(loadAccountSettings());
    }
  }, [
    dispatch,
    membershipsLoaded,
    membershipsLoading,
    accountSettingsLoaded,
    accountSettingsLoading,
  ]);

  const openLoginModal = async () => {
    await authenticationFlowService.initiateAuthentication({
      state: LegacyAuthModalState.LOGIN,
      allowedStates: [LegacyAuthModalState.LOGIN, LegacyAuthModalState.RESET_PASSWORD],
    });
  };

  // A reader who is in gets the feed's own sidebar; one who is not still gets the join card in
  // its place. Either way the post keeps a column beside it instead of stretching to full width.
  const showMemberSidebar = isAuthenticated && !!post?.userHasAccess;

  const getContainerClass = () => {
    const classes = ['single-post-info-container'];
    if (post && !post.userHasAccess) {
      classes.push('single-post-info-container-columns');
    } else if (showMemberSidebar) {
      // the feed's grid, so opening a post does not resize it
      classes.push('single-post-info-container-feed');
    }
    if (post?.publicPreview && !post?.userHasAccess) {
      classes.push('single-public-preview');
    }
    return classes.join(' ');
  };

  const { defaultMembership, defaultTier } = useMemo(() => {
    if (!memberships?.length) {
      return { defaultMembership: null, defaultTier: null };
    }

    const freeMemberships = memberships.filter((m) => m.tiers?.some((t) => t.freeTier));

    const defaultMembership: Membership =
      freeMemberships.length > 0 ? freeMemberships[0] : memberships[0];

    let defaultTier: Tier | undefined;
    if (defaultMembership) {
      defaultTier = defaultMembership.tiers?.find((t) => t.freeTier);
      if (!defaultTier) {
        defaultTier = defaultMembership.tiers?.[0];
      }
    }

    return { defaultMembership, defaultTier };
  }, [memberships]);

  const renderUserImage = () => {
    if (defaultMembership?.imageUrl) {
      return <img src={defaultMembership.imageUrl} alt={t('perks.membershipImageAlt')} />;
    }
    if (defaultTier?.imageUrl) {
      return <img src={defaultTier.imageUrl} alt={t('perks.tierImageAlt')} />;
    }
    if (post?.authors[0]?.imageUrl) {
      return <img src={post.authors[0].imageUrl} alt={t('perks.authorImageAlt')} />;
    }
    return null;
  };

  // The join card mirrors the landing page's editable Join page text
  // (Community Settings: Headline / Subheading / Returning member link) —
  // the API supplies the defaults, same as the landing page.
  const renderUserInfo = () => (
    <>
      {renderUserImage()}
      {accountSettings?.header && <h4 class="single-join-headline">{accountSettings.header}</h4>}
      {accountSettings?.subheader && (
        <p
          class="single-join-subheading"
          dangerouslySetInnerHTML={{ __html: accountSettings.subheader }}
        />
      )}
    </>
  );

  if (showLoading) {
    return (
      <section class="single-loading">
        <SpinnerComponent />
      </section>
    );
  }

  if (!loaded || !post) {
    return null;
  }

  return (
    <div class={getContainerClass()}>
      <div class="single-posts-left-column">
        {/* inside the post's column, as on the feed, so the link does not take a grid row of its
            own and push the sidebar down when a post opens from the feed */}
        {isAuthenticated && (
          <div class="go-back">
            <Link to="/posts">
              <span>{t('perks.backToFeed')}</span>
            </Link>
          </div>
        )}
        <PerksPostComponent key={post.id} post={post} collapseLongPosts={false} />
        {post?.commentsEnabled && !post?.hidden && (
          <div ref={commentsRef}>
            <PerksPostComments post={post} />
          </div>
        )}
      </div>

      {showMemberSidebar && (
        <PerksPostsSidebar
          onCreatePost={() => {
            // the submission form only exists on the feed; hand the intent over and route there
            requestSubmissionForm();
            setLocation('/posts');
          }}
        />
      )}

      {post && !post.userHasAccess && (
        <div class="single-posts-right-column single-posts-sticky">
          <div class="single-user-info">
            {renderUserInfo()}
            <div class="single-post-cta">
              <NeedAccessButton
                contentType={NeedAccessContentType.POST}
                contentId={post.id}
                showLock={true}
                message={t('access.joinToUnlock')}
              />
            </div>
            {!isAuthenticated && (
              <p class="single-login-header">
                <span class="single-login-header-text">
                  {accountSettings?.alreadyMemberText || t('common.alreadyMember')}
                </span>{' '}
                <span class="single-login-link" onClick={openLoginModal}>
                  {t('auth.logIn')}
                </span>
              </p>
            )}
          </div>
          <div class="single-popular-posts">
            <PerksPopularPosts />
          </div>
        </div>
      )}
      {musicHasActivated && <MobilePostCarousel />}
    </div>
  );
};
